import React, { useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { ChevronRight } from 'lucide-react';
import { useTimezone } from '../../contexts/TimezoneContext';
import type {
  AggregatePerformanceBucket,
  HistoricalBucketRow,
  ImpactAlert,
} from './types';
import {
  formatDuration,
  formatGap,
  formatNumber,
  formatSigned,
  formatWithUnit,
  formatZScore,
  formatImpact,
  formatMonthDay,
  titleCaseSlug,
  severityStyle,
  twoSidedSignificance,
} from './format';

interface TierMatrixProps {
  tiers: AggregatePerformanceBucket[];
}

type MetricKind = 'volume' | 'pace' | 'field';

interface MetricRow {
  key: string;
  kind: MetricKind;
  name: string;
  sub: string;
  slug?: string;
}

const tierNumber = (bucket?: string): number => {
  const m = bucket?.match(/Tier(\d+)$/);
  return m ? parseInt(m[1], 10) : 0;
};

const tierShortLabel = (bucket?: string): string => {
  const n = tierNumber(bucket);
  return n > 0 ? `T${n}` : '—';
};

/** The impact entry for a given basis (field slug or the literal interArrivalTimeMean). */
const impactFor = (tier: AggregatePerformanceBucket, basis: string): ImpactAlert | undefined =>
  tier.estimatedImpactValue?.find((a) => a.basedOnFieldName === basis);

const fieldUnitOf = (tiers: AggregatePerformanceBucket[], slug: string): string | null | undefined => {
  for (const t of tiers) {
    const u = impactFor(t, slug)?.unitOfMeasure;
    if (u !== undefined) return u;
  }
  return null;
};

/** Tier carrying the shared field baseline (Tier 5 / highest-N with historicalFieldMetrics). */
const baselineFieldTier = (
  tiers: AggregatePerformanceBucket[],
): AggregatePerformanceBucket | undefined =>
  tiers.find((t) => t.historicalFieldMetrics && Object.keys(t.historicalFieldMetrics).length > 0);

const isBaselineTier = (tier: AggregatePerformanceBucket, tiers: AggregatePerformanceBucket[]): boolean =>
  tierNumber(tier.bucket) === Math.max(...tiers.map((t) => tierNumber(t.bucket)));

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

const NoRead: React.FC = () => (
  <span className="font-semibold text-muted-foreground">
    <span className="mr-1 inline-block h-2 w-2 rounded-full border border-dashed border-muted-foreground align-[-1px]" />
    no read
  </span>
);

/** now-vs-historical comparison bar, mapping [hist−2sd, hist+2sd] → 6%..94%. */
const CompareBar: React.FC<{ now: number; hist: number; sd: number; tone: string }> = ({
  now,
  hist,
  sd,
  tone,
}) => {
  const lo = hist - 2 * sd;
  const hi = hist + 2 * sd;
  const pct = (v: number): number => {
    const p = hi === lo ? 50 : ((v - lo) / (hi - lo)) * 88 + 6;
    return Math.max(2, Math.min(98, p));
  };
  return (
    <div className="relative my-1.5 h-1.5 rounded-full bg-muted">
      <span
        className="absolute top-1/2 h-3 w-0.5 -translate-y-1/2 rounded-sm bg-muted-foreground/80"
        style={{ left: `${pct(hist).toFixed(1)}%` }}
      />
      <span
        className={clsx('absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full', tone)}
        style={{ left: `${pct(now).toFixed(1)}%` }}
      />
    </div>
  );
};

const MiniBars: React.FC<{ series: number[]; values?: string }> = ({ series, values }) => {
  const max = Math.max(...series, 1);
  return (
    <>
      <div className="my-1.5 flex items-end gap-0.5" style={{ height: 26 }}>
        {series.map((v, i) => (
          <div
            key={i}
            title={String(v)}
            className={clsx(
              'min-w-[3px] flex-1 rounded-t-sm',
              v === 0 ? 'border border-border bg-muted' : 'bg-primary/55',
            )}
            style={{ height: v === 0 ? 5 : Math.max(7, (v / max) * 26) }}
          />
        ))}
      </div>
      {values && <div className="font-mono text-[9.5px] text-muted-foreground">{values}</div>}
    </>
  );
};

const Disclosure: React.FC<{
  label: string;
  level: 2 | 3;
  children: React.ReactNode;
}> = ({ label, level, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={clsx(
        'mt-2 rounded-md border border-border',
        level === 2 ? 'bg-card' : 'bg-muted/20',
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 px-2 py-1.5 text-left text-[10.5px] font-semibold text-info"
      >
        <ChevronRight className={clsx('h-3 w-3 transition-transform', open && 'rotate-90')} />
        <span>{label}</span>
      </button>
      {open && <div className="px-2 pb-2 text-[10.5px] leading-relaxed text-muted-foreground">{children}</div>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Level 3 — raw baseline-bucket table
// ---------------------------------------------------------------------------

interface BaselineBucketsTableProps {
  buckets: HistoricalBucketRow[];
  emph: 'count' | 'gap';
  windowLabel: string;
}

const BaselineBucketsTable: React.FC<BaselineBucketsTableProps> = ({ buckets, emph, windowLabel }) => {
  const meanCount = buckets.reduce((a, b) => a + b.count, 0) / buckets.length;
  const gaps = buckets.map((b) => b.interArrivalTimeMean);
  const meanGap = gaps.reduce((a, b) => a + b, 0) / gaps.length;
  const variance =
    gaps.reduce((a, b) => a + (b - meanGap) ** 2, 0) / Math.max(1, gaps.length - 1);
  const gapSd = Math.sqrt(variance);

  return (
    <Disclosure label={`View the ${buckets.length} baseline buckets`} level={3}>
      <table className="w-full border-collapse text-[10px]">
        <thead>
          <tr className="border-b border-border text-[9px] uppercase tracking-wide text-muted-foreground">
            <th className="px-1.5 py-1 text-left font-semibold">Week</th>
            <th className="px-1.5 py-1 text-left font-semibold">Window</th>
            <th className={clsx('px-1.5 py-1 text-right font-semibold', emph === 'count' && 'text-info')}>
              Count
            </th>
            <th className={clsx('px-1.5 py-1 text-right font-semibold', emph === 'gap' && 'text-info')}>
              Avg gap
            </th>
          </tr>
        </thead>
        <tbody>
          {buckets.map((b, i) => (
            <tr key={i} className={clsx('border-b border-border/60', b.count === 0 && 'bg-muted/40')}>
              <td className="px-1.5 py-1 font-semibold text-foreground">
                {formatMonthDay(b.from)}
                {b.count === 0 && <span className="ml-1 font-bold text-warn">○</span>}
              </td>
              <td className="px-1.5 py-1 font-mono text-muted-foreground">{windowLabel}</td>
              <td className={clsx('px-1.5 py-1 text-right', emph === 'count' && 'font-bold text-foreground')}>
                {b.count}
              </td>
              <td className={clsx('px-1.5 py-1 text-right', emph === 'gap' && 'font-bold text-foreground')}>
                {b.count === 0 ? '—' : formatGap(b.interArrivalTimeMean)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-border bg-accent/30 font-semibold text-foreground">
            <td className="px-1.5 py-1 text-[9.5px] uppercase tracking-wide text-muted-foreground" colSpan={2}>
              Baseline (mean of {buckets.length})
            </td>
            <td className="px-1.5 py-1 text-right">{meanCount.toFixed(2)}</td>
            <td className="px-1.5 py-1 text-right">
              {formatGap(meanGap)} ± {Math.round(gapSd)}s
            </td>
          </tr>
        </tfoot>
      </table>
      <div className="mt-1.5 text-[9.5px]">
        Each week is the same clock window on a prior same-weekday occurrence; ○ marks weeks with 0
        transactions.
      </div>
    </Disclosure>
  );
};

// ---------------------------------------------------------------------------
// Level 0 cell
// ---------------------------------------------------------------------------

const CellL0: React.FC<{
  metric: MetricRow;
  tier: AggregatePerformanceBucket;
}> = ({ metric, tier }) => {
  if (metric.kind === 'volume') {
    const hist = tier.historicalCountMean;
    const delta = hist != null ? tier.count - hist : null;
    return (
      <>
        <div className="text-[13.5px] font-bold tracking-tight text-foreground">
          {tier.count}
          {delta != null && (
            <span
              className={clsx('ml-1 text-[11px] font-semibold', delta < 0 ? 'text-negative' : 'text-success')}
            >
              {formatSigned(delta, 1)}
            </span>
          )}
        </div>
        <div className="mt-px text-[9.5px] uppercase tracking-wide text-muted-foreground">count · z —</div>
      </>
    );
  }

  if (metric.kind === 'pace') {
    const z = tier.interArrivalTimeMeanZScore ?? null;
    const sevLevel =
      tier.interArrivalTimeMeanStatisticalSignificanceTest ?? twoSidedSignificance(z);
    const sev = severityStyle(sevLevel);
    const impact = impactFor(tier, 'interArrivalTimeMean');
    return (
      <>
        <div className="text-[13.5px] font-bold tracking-tight text-foreground">
          {formatImpact(impact)}
        </div>
        <div className="mt-0.5 text-[11px] font-semibold">
          {z == null ? <NoRead /> : <span className={sev.text}>{formatZScore(z)}</span>}
        </div>
      </>
    );
  }

  // field
  const slug = metric.slug as string;
  const fm = tier.fieldMetrics?.[slug];
  const z = fm?.avgZScore ?? null;
  const sevLevel = fm?.avgSignificanceTest ?? twoSidedSignificance(z);
  const sev = severityStyle(sevLevel);
  const impact = impactFor(tier, slug);
  return (
    <>
      <div className="text-[13.5px] font-bold tracking-tight text-foreground">{formatImpact(impact)}</div>
      <div className="mt-0.5 text-[11px] font-semibold">
        {z == null ? <NoRead /> : <span className={sev.text}>{formatZScore(z)}</span>}
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Level 1 cell (now vs historical + nested L2/L3)
// ---------------------------------------------------------------------------

const CellL1: React.FC<{
  metric: MetricRow;
  tier: AggregatePerformanceBucket;
  tiers: AggregatePerformanceBucket[];
}> = ({ metric, tier, tiers }) => {
  const { formatTime } = useTimezone();
  const buckets = tier.historicalBuckets ?? [];
  const windowLabel =
    buckets.length > 0 ? `${formatTime(buckets[0].from)} → ${formatTime(buckets[0].to ?? buckets[0].from)}` : '—';

  if (metric.kind === 'volume') {
    const hist = tier.historicalCountMean ?? 0;
    const series = buckets.map((b) => b.count);
    const mean = series.length ? series.reduce((a, b) => a + b, 0) / series.length : hist;
    return (
      <>
        <div className="text-[12px]">
          <span className="font-bold text-foreground">now {tier.count}</span>{' '}
          <span className="text-muted-foreground">vs hist avg {hist.toFixed(2)}</span>
        </div>
        <CompareBar now={tier.count} hist={hist} sd={Math.max(1, hist * 0.4)} tone="bg-primary" />
        {series.length > 0 && (
          <Disclosure label="How is this historical avg calculated?" level={2}>
            The {series.length} historical weekly <b className="text-foreground">counts</b> for this
            tier, averaged:
            <MiniBars series={series} values={`[${series.join(', ')}]`} />
            <b className="text-foreground">mean = {mean.toFixed(2)}</b>
            <BaselineBucketsTable buckets={buckets} emph="count" windowLabel={windowLabel} />
          </Disclosure>
        )}
      </>
    );
  }

  if (metric.kind === 'pace') {
    const histMean = tier.historicalInterArrivalTimeMean ?? 0;
    const histSd = tier.historicalInterArrivalTimeStdDev ?? 0;
    const z = tier.interArrivalTimeMeanZScore ?? null;
    const sev = severityStyle(tier.interArrivalTimeMeanStatisticalSignificanceTest ?? twoSidedSignificance(z));
    const series = buckets.map((b) => b.interArrivalTimeMean);
    return (
      <>
        <div className="text-[12px]">
          <span className="font-bold text-foreground">now {formatGap(tier.interArrivalTimeMean)}</span>{' '}
          <span className="text-muted-foreground">
            vs hist {formatGap(histMean)} ± {formatGap(histSd)}
          </span>
        </div>
        <CompareBar now={tier.interArrivalTimeMean} hist={histMean} sd={Math.max(1, histSd)} tone={sev.dot} />
        {series.length > 0 && (
          <Disclosure label="How is this historical avg calculated?" level={2}>
            The {series.length} historical weekly <b className="text-foreground">inter-arrival means</b>{' '}
            (seconds), averaged:
            <MiniBars series={series.map((s) => Math.round(s))} values={`[${series.map((s) => Math.round(s)).join(', ')}]`} />
            <b className="text-foreground">
              mean ± stdDev = {formatGap(histMean)} ± {formatGap(histSd)}
            </b>
            <div className="mt-1">
              Methodology: <code className="rounded bg-muted px-1 py-px font-mono text-[10px] text-foreground">{tier.interArrivalTimeMeanZScoreMethodology ?? 'n/a'}</code>
              {tier.interArrivalTimeMeanZScoreMethodology === 'basedOnBucketBaseline'
                ? " (bucket's own history)"
                : ' (Tier-5 baseline — lower |z| wins)'}
            </div>
            <BaselineBucketsTable buckets={buckets} emph="gap" windowLabel={windowLabel} />
          </Disclosure>
        )}
      </>
    );
  }

  // field — measured against the shared Tier-5 baseline
  const slug = metric.slug as string;
  const fm = tier.fieldMetrics?.[slug];
  const avg = fm?.avg ?? null;
  const unit = fieldUnitOf(tiers, slug);
  const baseTier = baselineFieldTier(tiers);
  const hfm = baseTier?.historicalFieldMetrics?.[slug];
  const histMean = hfm?.normalizedMean ?? null;
  const histSd = hfm?.normalizedStdDev ?? 0;
  const n = hfm?.dataPointCount ?? 0;
  const trimmed = n > 10;

  const baseBuckets = baseTier?.historicalBuckets ?? [];
  const weekCounts = baseBuckets.map((b) => b.count);
  const wcSum = weekCounts.reduce((a, b) => a + b, 0);
  const baseWindowLabel =
    baseBuckets.length > 0
      ? `${formatTime(baseBuckets[0].from)} → ${formatTime(baseBuckets[0].to ?? baseBuckets[0].from)}`
      : '—';

  return (
    <>
      <div className="text-[12px]">
        <span className="font-bold text-foreground">
          now {avg != null ? formatWithUnit(avg, unit) : '—'}
        </span>{' '}
        <span className="text-muted-foreground">
          {histMean != null ? `vs hist ${formatWithUnit(histMean, unit)} ± ${formatNumber(histSd)}` : 'no baseline'}
        </span>
      </div>
      {avg != null && histMean != null && (
        <CompareBar now={avg} hist={histMean} sd={Math.max(1, histSd)} tone="bg-primary" />
      )}
      <div className="mt-1 text-[9.5px] text-muted-foreground">
        field z uses the Tier-5 baseline (shared across every tier)
      </div>
      {hfm && (
        <Disclosure label="How is this historical avg calculated?" level={2}>
          The field baseline isn&apos;t bucketed per-week — it <b className="text-foreground">is</b> the
          Tier-5 <b className="text-foreground">normalized</b> stat: a {trimmed ? '5%-trimmed' : 'plain'}{' '}
          mean of every data point across the historical windows below.
          <dl className="my-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
            <dt className="text-muted-foreground">data points (n)</dt>
            <dd className="m-0 font-medium text-foreground">{n}</dd>
            <dt className="text-muted-foreground">normalized sum</dt>
            <dd className="m-0 font-medium text-foreground">
              {hfm.normalizedSum != null ? formatWithUnit(hfm.normalizedSum, unit) : '—'}
            </dd>
            <dt className="text-muted-foreground">normalized mean ± sd</dt>
            <dd className="m-0 font-medium text-foreground">
              {histMean != null ? formatWithUnit(histMean, unit) : '—'} ± {formatNumber(histSd)}
            </dd>
          </dl>
          n {trimmed ? '> 10 → uses the trimmed normalizedMean' : '≤ 10 → uses a plain mean'}. Every
          tier&apos;s field z is measured against this single baseline.
          {weekCounts.length > 0 && (
            <div className="mt-1.5">
              Faithful link: <b className="text-foreground">dataPointCount = {n}</b>
              {wcSum === n && (
                <>
                  {' '}= the sum of the Tier-5 weekly counts ({weekCounts.join(' + ')} ={' '}
                  <b className="text-foreground">{wcSum}</b>)
                </>
              )}
              .
            </div>
          )}
          {baseBuckets.length > 0 && (
            <BaselineBucketsTable buckets={baseBuckets} emph="count" windowLabel={baseWindowLabel} />
          )}
        </Disclosure>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Matrix
// ---------------------------------------------------------------------------

const TierMatrix: React.FC<TierMatrixProps> = ({ tiers }) => {
  const { formatDateTime } = useTimezone();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const sortedTiers = useMemo(
    () => [...tiers].sort((a, b) => tierNumber(a.bucket) - tierNumber(b.bucket)),
    [tiers],
  );

  const metricRows = useMemo<MetricRow[]>(() => {
    const rows: MetricRow[] = [
      { key: 'volume', kind: 'volume', name: 'Volume', sub: 'count' },
      { key: 'pace', kind: 'pace', name: 'Pace', sub: 'inter-arrival' },
    ];
    const slugs = new Set<string>();
    sortedTiers.forEach((t) => Object.keys(t.fieldMetrics ?? {}).forEach((s) => slugs.add(s)));
    slugs.forEach((slug) => {
      const unit = fieldUnitOf(sortedTiers, slug);
      rows.push({
        key: `field:${slug}`,
        kind: 'field',
        name: titleCaseSlug(slug),
        sub: unit || 'no unit',
        slug,
      });
    });
    return rows;
  }, [sortedTiers]);

  const toggle = (key: string): void =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <section>
      <div className="mb-2 flex items-center gap-2 text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground">
        <span className="text-primary">B</span> Tier matrix — the trend up to the last beat
        <span className="font-medium normal-case tracking-normal text-muted-foreground">
          · click a metric to expand
        </span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[620px] border-collapse">
          <thead>
            <tr>
              <th className="border-b border-border bg-muted/50 px-3 py-2 text-left align-bottom text-[11px] font-semibold text-foreground">
                Metric ↓ / Tier →
              </th>
              {sortedTiers.map((t) => {
                const baseline = isBaselineTier(t, sortedTiers);
                return (
                  <th
                    key={t.bucket ?? t.from}
                    title={`${formatDateTime(t.from)} → ${formatDateTime(t.to)}`}
                    className={clsx(
                      'border-b border-l border-border px-3 py-2 text-left align-bottom text-[11px]',
                      baseline ? 'bg-muted/70 text-muted-foreground' : 'bg-muted/50',
                    )}
                  >
                    <div className="text-[12px] font-bold tracking-tight">{tierShortLabel(t.bucket)}</div>
                    <div className="mt-px text-[10.5px] font-medium text-muted-foreground">
                      {formatDuration(t.duration)}
                    </div>
                    {baseline && (
                      <div className="mt-0.5 text-[9.5px] uppercase tracking-wide text-muted-foreground">
                        baseline · context
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {metricRows.map((m) => {
              const isOpen = expanded.has(m.key);
              return (
                <React.Fragment key={m.key}>
                  <tr>
                    <th className="border-b border-border p-0 align-middle">
                      <button
                        type="button"
                        onClick={() => toggle(m.key)}
                        className="flex w-full items-center gap-1.5 px-3 py-2.5 text-left hover:bg-muted/35"
                      >
                        <ChevronRight
                          className={clsx('h-3 w-3 flex-none text-muted-foreground transition-transform', isOpen && 'rotate-90')}
                        />
                        <span>
                          <span className="block text-[12.5px] font-semibold text-foreground">{m.name}</span>
                          <span className="block text-[10px] text-muted-foreground">{m.sub}</span>
                        </span>
                      </button>
                    </th>
                    {sortedTiers.map((t) => (
                      <td
                        key={t.bucket ?? t.from}
                        className={clsx(
                          'border-b border-l border-border px-3 py-2.5 align-top',
                          isBaselineTier(t, sortedTiers) && 'bg-muted/35',
                        )}
                      >
                        <CellL0 metric={m} tier={t} />
                      </td>
                    ))}
                  </tr>
                  {isOpen && (
                    <tr>
                      <th className="border-b border-border p-0">
                        <div className="px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                          now vs historical
                        </div>
                      </th>
                      {sortedTiers.map((t) => (
                        <td
                          key={t.bucket ?? t.from}
                          className={clsx(
                            'border-b border-l border-border px-3 py-2.5 align-top',
                            isBaselineTier(t, sortedTiers) ? 'bg-muted/45' : 'bg-accent/30',
                          )}
                        >
                          <CellL1 metric={m} tier={t} tiers={sortedTiers} />
                        </td>
                      ))}
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-2 px-0.5 text-[10.5px] text-muted-foreground">
        Tier severity (two-sided |z|): Normal &lt;2 · Caution 2–3 · Dangerous 3–4 · ALERT! &gt;4.
        Null z = no statistical read.
      </div>
    </section>
  );
};

export default TierMatrix;
